import React, { useEffect, useRef } from "react";

const toc = [
  ["From Ray Casting to RRT", "intro"],
  ["Shadow Rays", "shadow-rays"],
  ["Direct Self-Shadow", "self-shadow"],
  ["Shading with Shadows", "shading-shadows"],
  ["Self-Intersection", "self-intersection"],
  ["Hard vs. Soft Shadows", "hard-soft-shadows"],
  ["Reflection", "reflection"],
  ["Implementing Reflection", "reflection-impl"],
  ["Refraction & Dielectrics", "refraction"],
  ["Index of Refraction", "ior"],
  ["Snell's Law", "snells-law"],
  ["Total Internal Reflection", "tir"],
  ["Numerical Precision", "numerical"],
  ["Fresnel Effect", "fresnel"],
  ["Schlick Approximation", "schlick"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const Note = ({ label, children }) => (
  <div className="note-box">
    {label && <div className="note-label">{label}</div>}
    {children}
  </div>
);

const Warn = ({ label, children }) => (
  <div className="note-box" style={{ background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.3)" }}>
    {label && <div className="note-label" style={{ color: "#f87171" }}>{label}</div>}
    {children}
  </div>
);

const RecursiveRayTracing = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Recursive Ray Tracing</h1>
      <p className="subtitle">
        Shadow, Reflection, and Refraction &mdash; by Jeff Wilson, Ph.D.
      </p>

      {/* ── INTRO ── */}
      <h2 id="intro">From Ray Casting to Recursive Ray Tracing</h2>
      <p>
        Basic <strong>ray casting</strong> traces a ray from the eye to the
        first surface it hits and computes direct illumination at that point.
        This models the simple light path:
      </p>
      <F>{"$$\\text{Light} \\;\\rightarrow\\; \\text{Surface} \\;\\rightarrow\\; \\text{Eye}$$"}</F>
      <p>
        Ray casting cannot handle occlusion (shadows), light bouncing off other
        surfaces (reflections), or light bending through transparent materials
        (refractions). The goal of recursive ray tracing is to extend the
        framework with <strong>secondary rays</strong>:
      </p>
      <ul>
        <li><strong>Shadow rays</strong> — cast toward each light to test occlusion.</li>
        <li><strong>Reflection rays</strong> — cast in the mirror direction to capture specular appearance.</li>
        <li><strong>Refraction rays</strong> — bent through the surface according to Snell's law.</li>
      </ul>
      <p>
        Each secondary ray is evaluated recursively by the same{" "}
        <code>raycolor</code> function, so reflections of reflections, shadows
        inside refractions, and other complex interactions arise naturally.
      </p>

      {/* ── SHADOW RAYS ── */}
      <h2 id="shadow-rays">Shadow Rays</h2>
      <p>
        When a primary ray hits surface point {"$\\mathbf{p}$"}, a{" "}
        <strong>shadow ray</strong> is cast toward each light source in
        direction {"$\\hat{\\mathbf{l}}$"}. If the ray intersects any geometry
        before reaching the light, that light contributes no illumination.
      </p>
      <F>
        {"$$\\mathbf{r}(t) = \\mathbf{p} + t\\,\\hat{\\mathbf{l}}, \\qquad t \\in [\\epsilon,\\; t_{\\max}]$$"}
      </F>
      <ul>
        <li>
          {"$\\epsilon$"} — small offset to prevent self-intersection (shadow
          acne). Alternatively offset the origin: {"$\\mathbf{p}_{\\text{origin}} = \\mathbf{p} + \\epsilon\\,\\mathbf{n}$"}.
        </li>
        <li>
          {"$t_{\\max}$"} — distance to the light, so occluders beyond the
          light are ignored.
        </li>
      </ul>

      {/* ── DIRECT SELF-SHADOW ── */}
      <h2 id="self-shadow">Direct Self-Shadow</h2>
      <p>
        Before spawning a shadow ray, perform a cheap pre-check. If the surface
        normal {"$\\mathbf{n}$"} faces away from the light direction{" "}
        {"$\\mathbf{l}$"}, the surface is inherently in shadow:
      </p>
      <F>{"$$\\text{self-shadow when} \\quad \\mathbf{n} \\cdot \\mathbf{l} < 0$$"}</F>
      <p>
        This single dot product eliminates shadow ray computation for all
        back-facing surfaces with no loss of accuracy.
      </p>

      {/* ── SHADING WITH SHADOWS ── */}
      <h2 id="shading-shadows">Shading Adjustment with Shadows</h2>
      <p>
        Incorporating shadow tests into Blinn-Phong multi-light shading:
      </p>
      <F>
        {"$$c = c_a c_r + \\sum_{i}\\,\\mathbf{1}_{\\text{lit}_i}\\bigl(\\,c_{l_i} c_r \\max(0, \\mathbf{n}\\cdot\\mathbf{l}_i) + c_{l_i} c_p \\max(0, \\mathbf{n}\\cdot\\mathbf{h}_i)^s\\bigr)$$"}
      </F>
      <ul>
        <li>
          {"$\\mathbf{1}_{\\text{lit}_i} = 1$"} if the shadow ray to light{" "}
          {"$i$"} is unoccluded, {"$0$"} otherwise.
        </li>
        <li>
          The ambient term {"$c_a c_r$"} is always included to prevent
          completely black shadows.
        </li>
      </ul>

      {/* ── SELF-INTERSECTION ── */}
      <h2 id="self-intersection">Self-Intersection (Shadow Acne)</h2>
      <p>
        Without an {"$\\epsilon$"} offset, a shadow ray will re-intersect its
        own surface at {"$t \\approx 0$"}, producing false occlusion — a
        speckled artifact called <strong>shadow acne</strong>.
      </p>
      <Note label="Fix 1 — Epsilon Offset">
        Shift the ray origin along the surface normal:{" "}
        {"$\\mathbf{p}_{\\text{origin}} = \\mathbf{p} + \\epsilon\\,\\mathbf{n}$"}.
        Choose {"$\\epsilon$"} carefully — too small and acne persists; too
        large and shadows detach from contact edges.
      </Note>
      <Note label="Fix 2 — Ignore List / Object Flags">
        Exclude the source object from intersection tests using an ID or flag.
        Only reliable for <em>convex</em> objects — concave surfaces can
        legitimately self-shadow at non-trivial distances. Must be paired with
        the direct self-shadow pre-check.
      </Note>

      {/* ── HARD/SOFT SHADOWS ── */}
      <h2 id="hard-soft-shadows">Hard vs. Soft Shadows</h2>
      <p>
        A <strong>point light</strong> produces perfectly sharp{" "}
        <em>hard shadows</em>. An <strong>area light</strong> has spatial
        extent, creating a gradual penumbra. To render soft shadows, cast
        multiple shadow rays toward different sample positions on the light
        surface:
      </p>
      <F>{"$$\\text{shadow factor} = \\frac{\\text{unblocked rays}}{N_{\\text{samples}}}$$"}</F>
      <Note label="Coming Up">
        This multi-sample approach is a special case of{" "}
        <strong>distribution ray tracing</strong>, which generalises it to
        depth of field, motion blur, and glossy reflections.
      </Note>

      {/* ── REFLECTION ── */}
      <h2 id="reflection">Reflection</h2>
      <p>
        <strong>Ideal specular reflection</strong> obeys the law of reflection:
        angle of incidence equals angle of reflection, both measured from the
        surface normal. Given incoming direction {"$\\mathbf{V}$"} and normal{" "}
        {"$\\mathbf{N}$"}:
      </p>
      <F>{"$$\\mathbf{R} = \\mathbf{V} - 2(\\mathbf{V}\\cdot\\mathbf{N})\\,\\mathbf{N}$$"}</F>
      <p>
        The formula decomposes {"$\\mathbf{V}$"} into normal and tangential
        components, then flips only the normal component. The reflected ray is
        launched recursively — whatever colour it returns is the mirror image
        the surface reflects.
      </p>

      {/* ── REFLECTION IMPL ── */}
      <h2 id="reflection-impl">Implementing Reflection</h2>
      <ol>
        <li>
          <strong>Compute the reflected direction</strong> using the incoming
          ray {"$\\mathbf{d}$"} and surface normal {"$\\mathbf{n}$"}:
          <F>{"$$\\mathbf{r} = \\mathbf{d} - 2(\\mathbf{d}\\cdot\\mathbf{n})\\,\\mathbf{n}$$"}</F>
        </li>
        <li>
          <strong>Offset the origin</strong> to avoid self-intersection:
          <F>
            {"$\\mathbf{p}_{\\text{origin}} = \\mathbf{p} + \\epsilon\\,\\mathbf{n}$"}
            {" or "}
            {"$\\mathbf{p}_{\\text{origin}} = \\mathbf{p} + \\epsilon\\,\\mathbf{r}$"}
          </F>
          Normal-direction offset is generally preferred but introduces a small
          positional error at grazing angles.
        </li>
        <li>
          <strong>Recurse</strong>:
          <F>
            {"$$c \\;\\mathrel{+}=\\; k_m \\cdot \\texttt{raycolor}(\\mathbf{p}_{\\text{origin}},\\;\\mathbf{r},\\;\\epsilon,\\;\\infty)$$"}
          </F>
          {"$k_m \\in [0,1]^3$"} is the per-channel specular reflection
          coefficient.
        </li>
        <li>
          <strong>Limit recursion depth</strong> to avoid infinite loops in
          closed mirror environments (typically 4–8 bounces).
        </li>
      </ol>
      <Note label="Practical Considerations">
        <ul>
          <li><strong>Energy loss.</strong> Real surfaces absorb some light; some metals (e.g. gold) colour-shift the reflection. Keep {"$k_m < 1$"}.</li>
          <li><strong>Skip if {"$k_m = \\mathbf{0}$"}.</strong> No reflectance → no ray needed.</li>
          <li><strong>Recursion cap.</strong> Contribution falls off rapidly ({"$k_m^n$"} per bounce), so a modest cap loses little visible energy.</li>
        </ul>
      </Note>

      {/* ── REFRACTION ── */}
      <h2 id="refraction">Refraction &amp; Dielectrics</h2>
      <p>
        <strong>Refraction</strong> is the bending of light as it crosses a
        boundary between two media with different optical densities. Light slows
        inside a denser medium; the wavefront tilts to accommodate the speed
        change, altering the ray's direction.
      </p>
      <p>
        Materials that transmit visible light are called{" "}
        <strong>dielectrics</strong>. They are non-conductive and have a
        bandgap large enough that visible photons cannot excite electrons, so
        most light transmits through. At any dielectric surface two things
        happen simultaneously: a <em>refracted (transmitted) ray</em> enters
        the new medium, and a <em>partially reflected ray</em> bounces back.
      </p>

      {/* ── IOR ── */}
      <h2 id="ior">Index of Refraction (IOR)</h2>
      <p>
        The <strong>index of refraction</strong> {"$n$"} quantifies how much a
        material slows light:
      </p>
      <F>{"$$n = \\frac{c_{\\text{vacuum}}}{c_{\\text{material}}}$$"}</F>
      <p>
        Because light can only slow down, {"$n \\geq 1$"} always. Common
        values:
      </p>
      <table>
        <thead><tr><th>Material</th><th>IOR</th></tr></thead>
        <tbody>
          <tr><td>Vacuum</td><td>1.000</td></tr>
          <tr><td>Air</td><td>1.0003</td></tr>
          <tr><td>Ice</td><td>1.309</td></tr>
          <tr><td>Water</td><td>1.33</td></tr>
          <tr><td>Crown glass</td><td>1.50</td></tr>
          <tr><td>Quartz</td><td>1.544</td></tr>
          <tr><td>Diamond</td><td>2.414</td></tr>
        </tbody>
      </table>

      {/* ── SNELL'S LAW ── */}
      <h2 id="snells-law">Snell's Law &amp; the Refracted Ray Direction</h2>
      <p>
        <strong>Snell's Law</strong> relates the angle of incidence {"$\\theta$"}{" "}
        to the angle of transmission {"$\\phi$"} at the boundary:
      </p>
      <F>{"$$n \\sin\\theta = n_t \\sin\\phi$$"}</F>
      <p>
        The refracted ray direction {"$\\mathbf{t}$"} is derived by decomposing
        the incoming direction {"$\\mathbf{d}$"} into normal and tangential
        parts:
      </p>
      <F>
        {"$$\\mathbf{t} = \\frac{n}{n_t}\\!\\left(\\mathbf{d} - \\mathbf{n}(\\mathbf{d}\\cdot\\mathbf{n})\\right) - \\mathbf{n}\\sqrt{1 - \\left(\\frac{n}{n_t}\\right)^{\\!2}\\!\\bigl(1-(\\mathbf{d}\\cdot\\mathbf{n})^2\\bigr)}$$"}
      </F>
      <Note label="Entering vs. Exiting">
        When the ray is <em>entering</em> a medium,{" "}
        {"$\\mathbf{d}\\cdot\\mathbf{n} < 0$"}. When <em>exiting</em>,{" "}
        {"$\\mathbf{d}\\cdot\\mathbf{n} > 0$"}. In the exiting case, flip{" "}
        {"$\\mathbf{n}$"} and swap {"$n \\leftrightarrow n_t$"}.
      </Note>

      {/* ── TIR ── */}
      <h2 id="tir">Total Internal Reflection</h2>
      <p>
        When a ray travels from a denser medium toward a less-dense one (
        {"$n > n_t$"}), and the angle exceeds the <strong>critical angle</strong>{" "}
        {"$\\theta_c$"}, no refracted ray can exit — all energy is reflected
        internally:
      </p>
      <F>{"$$\\sin\\theta_c = \\frac{n_t}{n}, \\qquad \\theta_c = \\arcsin\\!\\left(\\frac{n_t}{n}\\right)$$"}</F>
      <p>
        <strong>Detecting TIR in code</strong> — check the radicand of the
        refracted ray formula:
      </p>
      <F>{"$$\\Delta = 1 - \\left(\\frac{n}{n_t}\\right)^{\\!2}\\!\\bigl(1-(\\mathbf{d}\\cdot\\mathbf{n})^2\\bigr)$$"}</F>
      <p>
        If {"$\\Delta < 0$"}: TIR — skip the refraction ray, process only the
        reflection ray.
      </p>
      <p><strong>Full implementation steps:</strong></p>
      <ol>
        <li>Compute {"$\\cos\\theta = -(\\mathbf{d}\\cdot\\mathbf{n})$"}.</li>
        <li>Compute {"$\\Delta = 1-(n/n_t)^2(1-\\cos^2\\!\\theta)$"}.</li>
        <li>If {"$\\Delta < 0$"}: TIR — terminate or compute reflection only.</li>
        <li>Compute {"$\\mathbf{t}$"} using the formula above.</li>
        <li>Offset origin: {"$\\mathbf{p}_{\\text{origin}} = \\mathbf{p} - \\epsilon\\,\\mathbf{n}$"} (into the new medium).</li>
        <li>Recurse: {"$c \\mathrel{+}= k_t \\cdot \\texttt{raycolor}(\\mathbf{p}_{\\text{origin}}, \\mathbf{t}, \\epsilon, \\infty)$"}.</li>
      </ol>

      {/* ── NUMERICAL ── */}
      <h2 id="numerical">Numerical Precision</h2>
      <Warn label="Nearly equal indices ($n_t \\approx n$)">
        When two media have nearly identical IOR,{" "}
        {"$(n/n_t)^2 \\approx 1$"} and intermediate values can amplify
        floating-point rounding. Clamp {"$\\Delta$"} to zero from below before
        taking the square root.
      </Warn>
      <Warn label="Near-grazing incidence ($\\mathbf{d}\\cdot\\mathbf{n} \\approx 0$)">
        Small errors in {"$\\mathbf{d}$"} or {"$\\mathbf{n}$"} can flip the
        entering/exiting sign, producing wildly wrong refracted directions.
        Special-case or clamp {"$|\\mathbf{d}\\cdot\\mathbf{n}|$"} below a
        small threshold.
      </Warn>

      {/* ── FRESNEL ── */}
      <h2 id="fresnel">Fresnel Effect</h2>
      <p>
        At every dielectric boundary, the fraction of light reflected vs.
        transmitted depends on the angle of incidence — this is the{" "}
        <strong>Fresnel Effect</strong>. At grazing angles, surfaces appear
        almost perfectly reflective; at normal incidence, reflection is minimal.
      </p>
      <p>
        The exact reflectance for unpolarised light is the average of two
        polarisation components:
      </p>
      <F>
        {"$$R_s = \\left(\\frac{n\\cos\\theta_i - n_t\\cos\\theta_t}{n\\cos\\theta_i + n_t\\cos\\theta_t}\\right)^{\\!2}$$"}
        {"$$R_p = \\left(\\frac{n_t\\cos\\theta_i - n\\cos\\theta_t}{n_t\\cos\\theta_i + n\\cos\\theta_t}\\right)^{\\!2}$$"}
        {"$$R(\\theta_i) = \\frac{R_s + R_p}{2}$$"}
      </F>
      <p>
        {"$R(\\theta_i)$"} is the reflected fraction; {"$1-R(\\theta_i)$"} is
        transmitted. The full equations require trig and multiple divisions —
        expensive for large ray counts.
      </p>

      {/* ── SCHLICK ── */}
      <h2 id="schlick">Schlick Approximation</h2>
      <p>
        The <strong>Schlick approximation</strong> replaces the full Fresnel
        equations with a cheap polynomial, accurate for smooth dielectrics:
      </p>
      <F>
        {"$$R(\\theta) = R_0 + (1 - R_0)(1 - \\cos\\theta)^5$$"}
        {"$$R_0 = \\left(\\frac{n_t - n}{n_t + n}\\right)^{\\!2}$$"}
      </F>
      <p>
        {"$R_0$"} is the reflectance at normal incidence; the{" "}
        {"$(1-\\cos\\theta)^5$"} factor smoothly interpolates to 1 at grazing
        angles.
      </p>
      <p><strong>Why Schlick?</strong></p>
      <ul>
        <li><strong>Efficiency</strong> — arithmetic only, no trig beyond a dot product.</li>
        <li><strong>Accuracy</strong> — closely matches the exact Fresnel curve for dielectrics.</li>
      </ul>
      <p><strong>Applications:</strong></p>
      <ul>
        <li>Blend specular reflection into Blinn-Phong in an angle-dependent way.</li>
        <li>
          Weight reflection and refraction rays at each transparent boundary:
          <F>{"$$c \\;\\mathrel{+}=\\; R(\\theta)\\cdot\\texttt{raycolor}(\\text{reflected}) \\;+\\; (1-R(\\theta))\\cdot\\texttt{raycolor}(\\text{refracted})$$"}</F>
        </li>
      </ul>
      <Note label="At normal incidence (air → glass)">
        {"$R_0 = ((1.5-1)/(1.5+1))^2 = 0.04$"} — only 4% of light is
        reflected when looking straight into glass. Consistent with everyday
        experience.
      </Note>
    </div>
  );
};

export default RecursiveRayTracing;
